
import { TradingSymbol } from "@/utils/mockData";
import { config } from '../config';

// Fetch symbols from API and adapt structure to TradingSymbol[]
const fetchSymbolsFromAPI = async (accessToken: string): Promise<TradingSymbol[]> => {
  try {
    const res = await fetch(`${config.apiUrl}symbols`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });
    
    if (!res.ok) {
      throw new Error(`Failed to fetch symbols: ${res.status} ${res.statusText}`);
    }
    
    const apiSymbols = await res.json();
    // console.log('e:', apiSymbols);
    
    // Adapt the data to TradingSymbol[]
    return apiSymbols.map((apiSym: any, i: number) => {
      // Updated helper function to accept negative values as valid
      const getValidValue = (primary: any, fallback: any, defaultValue = 0) => {
        // Accept any valid number (positive or negative), reject only null, undefined, empty string, or 0
        if (primary !== null && primary !== undefined && primary !== "" && primary !== 0) {
          return primary;
        }
        if (fallback !== null && fallback !== undefined && fallback !== "" && fallback !== 0) {
          return fallback;
        }
        return defaultValue;
      };

      // Use feed data as fallback for all price/volume related fields
      const feed = apiSym.feed || {};
      
      const buyPrice = getValidValue(apiSym.buyPrice, feed.b);
      const sellPrice = getValidValue(apiSym.sellPrice, feed.a);
      const high = getValidValue(apiSym.high, feed.h);
      const low = getValidValue(apiSym.low, feed.l);
      const open = getValidValue(apiSym.open, feed.o);
      const close = getValidValue(apiSym.close, feed.c);
      const bidVolume = getValidValue(apiSym.bidVolume, feed.tbq);
      const askVolume = getValidValue(apiSym.askVolume, feed.tsq);
      const lastTradeTime = getValidValue(apiSym.lastTradeTime, feed.ltt, Date.now());
      const change = getValidValue(apiSym.change, feed.chp);
      
      // Calculate spread if both prices are valid (can be negative)
      const spread = (buyPrice !== 0 && sellPrice !== 0) 
        ? Math.abs(sellPrice - buyPrice) 
        : 0;

      return {
        id: typeof apiSym.id === "number" ? apiSym.id : i,
        name: apiSym.name || apiSym.symbol, // Use name for display, fallback to symbol
        symbol: apiSym.symbol, // Keep symbol separate for websocket mapping
        buyPrice,
        sellPrice,
        spread,
        high,
        low,
        previousBuyPrice: buyPrice,
        previousSellPrice: sellPrice,
        previousHigh: high, // Initialize previous values
        previousLow: low,
        change: typeof change === 'number' ? parseFloat(change.toFixed(2)) : 0,
        intrumentType: apiSym.intrumentType ?? apiSym.instrumentType ?? "unknown",
        relatedSymbols: apiSym.related_symbols ?? apiSym.relatedSymbols ?? {},
        open,
        close,
        lastTradeTime,
        bidVolume,
        askVolume,
        openPrice: open,
        closePrice: close,
        digit: apiSym.digit, // Capture digit parameter for decimal formatting
        // Store original feed data for future reference
        _feedData: feed
      };
    });
  } catch (error) {
    console.error("API request failed:", error);
    throw error;
  }
};

export default fetchSymbolsFromAPI;
